import { useEntitySaver } from '@elentari/core/hooks/useEntitySaver'
import { Button, CircularProgress, Divider, Grid } from '@material-ui/core'
import { Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router'
import { Paper } from '../../../components'
import FieldsPending from '../../../components/FieldsPending'
import When from '../../../components/When'
import useCanSave from '../../../hooks/useCanSave'
import scrollToError from '../../../utils/scrollToError'
import yupValidation from '../../../utils/yupValidation'
import {
  alterarSituacao,
  cenariosRepository,
  updateCenario
} from '../hooks/cenariosRepository'
import { useCenario } from '../hooks/useCenario'
import { useCenarioMetadata } from '../hooks/useCenarioMetadata'
import { CenarioCondicao } from '../types'
import { buildCenarioSchema } from './cenarios.schema'
import { toFormValues, toPayload } from './cenarioForm.utils'
import { CondicoesInput } from './CondicoesInput'
import { IdentificacaoVigencia } from './IdentificacaoVigencia'
import { ValoresCenario } from './ValoresCenario'

const emptyInitialValues: {
  tipo: string
  descricao: string
  dataInicio: Date | null
  dataFim: Date | null
  ativo: boolean
  condicoes: CenarioCondicao[]
  valores: Record<string, any>
  _novaCondicaoTipo: string
  _novaCondicaoValor: any
} = {
  tipo: '',
  descricao: '',
  dataInicio: null,
  dataFim: null,
  ativo: true,
  condicoes: [],
  valores: {},
  _novaCondicaoTipo: '',
  _novaCondicaoValor: null
}

export const CenariosForm = () => {
  const history = useHistory()
  const [cenarioState] = useCenario()
  const [{ metadata }, metadataActions] = useCenarioMetadata()
  const [loading, setLoading] = useState<boolean>(false)
  const canSave = useCanSave(cenarioState, 'Cenários')
  const schema = buildCenarioSchema(metadata)

  useEffect(() => {
    metadataActions.fetchIfNeeded()
  }, []) // eslint-disable-line

  const { saveWithoutRedirect } = useEntitySaver<any>(async data => {
    const response =
      cenarioState.tag === 'with-data'
        ? await updateCenario(cenarioState.entity.id, data)
        : await cenariosRepository.save(data)
    setTimeout(() => setLoading(false), 1000)
    return response
  })

  async function handleSubmit(values: typeof emptyInitialValues) {
    setLoading(true)

    const ativoAntes =
      cenarioState.tag === 'with-data' ? cenarioState.entity.ativo : true

    const response = await saveWithoutRedirect(toPayload(values))

    if (!response.ok) {
      setLoading(false)
      return
    }

    const id =
      cenarioState.tag === 'with-data'
        ? cenarioState.entity.id
        : (response.data as any)?.id

    if (id && values.ativo !== ativoAntes) {
      await alterarSituacao(id, { ativo: values.ativo })
    }

    history.goBack()
  }

  const handleGoBack = () => history.goBack()

  const initialValues =
    cenarioState.tag === 'with-data'
      ? toFormValues(cenarioState.entity, metadata)
      : emptyInitialValues

  return (
    <Formik
      enableReinitialize
      validate={yupValidation(schema)}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, errors, submitCount, values }) => (
        <Form>
          <Paper>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <IdentificacaoVigencia
                  metadata={metadata}
                  tipoDisabled={cenarioState.tag === 'with-data'}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <CondicoesInput metadata={metadata} />
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <ValoresCenario metadata={metadata} tipo={values.tipo} />
              </Grid>
            </Grid>

            <FieldsPending errors={errors} submitCount={submitCount} />

            <Grid
              justifyContent="flex-end"
              item
              container
              spacing={4}
              style={{ marginTop: 8 }}
            >
              <Grid item xs={12}>
                <Grid justifyContent="flex-end" container spacing={2}>
                  <Grid item style={{ width: 160 }}>
                    <Button
                      fullWidth
                      type="button"
                      variant="outlined"
                      onClick={handleGoBack}
                      disabled={isSubmitting || loading}
                    >
                      Voltar
                    </Button>
                  </Grid>
                  <When value={canSave} equals>
                    <Grid item style={{ width: 160 }}>
                      <Button
                        data-testid="salvar"
                        fullWidth
                        type="submit"
                        variant="contained"
                        color="primary"
                        onClick={() => scrollToError(errors)}
                        disabled={isSubmitting || loading || !canSave}
                      >
                        {isSubmitting || loading ? (
                          <CircularProgress color="inherit" size={24} />
                        ) : (
                          'Salvar'
                        )}
                      </Button>
                    </Grid>
                  </When>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Form>
      )}
    </Formik>
  )
}
